import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import { useCallback, useMemo } from 'react';
import useAsyncData from '../../shared/hooks/useAsyncData';
import { adminEmployeesApi, Department, departmentsApi } from '../admin.api';
import ResourceManager, { ResourceColumn, ResourceField } from '../components/ResourceManager';

const columns: ResourceColumn[] = [
  { id: 'id', dataKey: 'id', label: 'ID', align: 'center', sortable: true },
  { id: 'employeeId', dataKey: 'employeeId', label: 'Employee ID', align: 'left', sortable: true },
  { id: 'position', dataKey: 'position', label: 'Position', align: 'left', sortable: true },
  {
    id: 'department',
    dataKey: 'department',
    label: 'Department',
    align: 'left',
    renderCell: (value: any) => value?.name ?? <span className='unavailable-message'>—</span>,
  },
  { id: 'joiningDate', dataKey: 'joiningDate', label: 'Joined', align: 'center', sortable: true },
  {
    id: 'employmentStatus',
    dataKey: 'employmentStatus',
    label: 'Status',
    align: 'center',
    sortable: true,
  },
  {
    id: 'currentSalary',
    dataKey: 'currentSalary',
    label: 'Salary',
    align: 'right',
    sortable: true,
    renderCell: (value: number) =>
      value == null ? <span className='unavailable-message'>—</span> : `₹ ${Number(value).toLocaleString('en-IN')}`,
  },
];

/**
 * Admin-side employee records — the `/api/admin/employees` CRUD surface, which is
 * distinct from the read-oriented HR directory at `/api/hr/employees`. Creating a
 * record links an existing user to a department, so both IDs are required.
 */
export default function EmployeeRecordsPage() {
  const fetchDepartments = useCallback(() => departmentsApi.list(), []);
  const { data: departments, error: deptError } = useAsyncData<Department[]>(
    fetchDepartments,
    [],
    {
      code: 'DEPARTMENTS_FOR_EMPLOYEE_FAILED',
      errorMessage: 'Failed to load departments',
      toastOnError: false,
    }
  );

  const fields: ResourceField[] = useMemo(
    () => [
      {
        name: 'user_id',
        label: 'User ID',
        type: 'number',
        required: true,
        helperText: 'Existing users.id this employee record belongs to',
        hideOnEdit: true,
      },
      {
        name: 'department_id',
        label: 'Department',
        type: 'select',
        required: true,
        options: departments.map((d) => ({ label: d.name, value: d.id })),
      },
      { name: 'employee_id', label: 'Employee Code', required: true, helperText: 'e.g. EMP-010' },
      { name: 'position', label: 'Position' },
      { name: 'joining_date', label: 'Joining Date', type: 'date', required: true },
      { name: 'current_salary', label: 'Current Salary', type: 'number' },
      { name: 'date_of_birth', label: 'Date of Birth', type: 'date' },
      {
        name: 'gender',
        label: 'Gender',
        type: 'select',
        options: [
          { label: 'Male', value: 'Male' },
          { label: 'Female', value: 'Female' },
          { label: 'Other', value: 'Other' },
        ],
      },
      { name: 'nationality', label: 'Nationality' },
      { name: 'aadhaar_number', label: 'Aadhaar Number' },
      { name: 'pan_number', label: 'PAN Number' },
    ],
    [departments]
  );

  return (
    <Stack gap={2}>
      {deptError && (
        <Alert severity='warning'>
          Departments could not be loaded, so the department picker is empty. {deptError}
        </Alert>
      )}

      <ResourceManager
        title='EMPLOYEE RECORDS'
        entityName='Employee'
        client={adminEmployeesApi}
        columns={columns}
        fields={fields}
        toFormValues={(row) => ({
          department_id: row.department?.id ?? '',
          employee_id: row.employeeId ?? '',
          position: row.position ?? '',
          joining_date: row.joiningDate ?? '',
          current_salary: row.currentSalary ?? '',
          date_of_birth: row.dateOfBirth ?? '',
          gender: row.gender ?? '',
          nationality: row.nationality ?? '',
          aadhaar_number: row.aadhaarNumber ?? '',
          pan_number: row.panNumber ?? '',
        })}
      />
    </Stack>
  );
}
